import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  FormControl,
  FormLabel,
  Select,
  Option,
  Alert,
  IconButton,
  Tooltip,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/joy';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  SwapHoriz as SwapIcon
} from '@mui/icons-material';
import { ChangeRequestService } from '../services/changeRequestService';
import { ChangeRequestWithDetails, ChangeRequestFilters, CreateChangeRequestData } from '../types/changeRequest';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../utils/permissions';
import ChangeRequestModal from '../components/ChangeRequestModal';

interface ChangeRequestsPageProps {
  onNavigate?: (path: string) => void;
}

const ChangeRequestsPage: React.FC<ChangeRequestsPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [changeRequests, setChangeRequests] = useState<ChangeRequestWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ChangeRequestFilters>({
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ChangeRequestWithDetails | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);

  const canCreate = hasPermission('change_requests.create');
  const canEdit = hasPermission('change_requests.edit');
  const canDelete = hasPermission('change_requests.delete');
  const canApprove = hasPermission('change_requests.approve');
  const canReject = hasPermission('change_requests.reject');

  useEffect(() => {
    loadChangeRequests();
  }, [filters]);

  const loadChangeRequests = async () => {
    try {
      setLoading(true);
      const response = await ChangeRequestService.getChangeRequests(filters);
      
      // Add null checks to prevent crashes
      if (response && response.changeRequests) {
        setChangeRequests(response.changeRequests);
      } else {
        setChangeRequests([]);
      }
      
      if (response && response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load change requests');
      console.error('Error loading change requests:', err);
      setChangeRequests([]);
      setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof ChangeRequestFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
  //   setFilters(prev => ({ ...prev, page }));
  // };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 20
    });
  };

  const handleViewDetails = (request: ChangeRequestWithDetails) => {
    setSelectedRequest(request);
    setShowDetailsDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to cancel this change request?')) {
      return;
    }

    try {
      await ChangeRequestService.deleteChangeRequest(id);
      loadChangeRequests();
    } catch (err) {
      setError('Failed to cancel change request');
      console.error('Error cancelling change request:', err);
    }
  };

  const handleApprove = async (id: number) => {
    const comments = window.prompt('Enter approval comments (optional):');
    try {
      const result = await ChangeRequestService.approveChangeRequest(id, comments || undefined);
      
      // Check if there's a warning about schedule update
      if (result.warning) {
        alert(`Change request approved successfully!\n\nWarning: ${result.warning}\n\nPlease check the schedule manually.`);
      } else {
        let message = 'Change request approved successfully!';
        if (result.updatedSchedule) {
          message += `\n\nSchedule has been updated with status: ${result.updatedSchedule.status}`;
        }
        alert(message);
      }
      
      loadChangeRequests();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to approve change request';
      setError(errorMessage);
      console.error('Error approving change request:', err);
    }
  };

  const handleReject = async (id: number) => {
    const comments = window.prompt('Enter rejection comments (required):');
    if (!comments) {
      alert('Comments are required when rejecting a change request');
      return;
    }

    try {
      await ChangeRequestService.rejectChangeRequest(id, comments);
      alert('Change request rejected successfully!');
      loadChangeRequests();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to reject change request';
      setError(errorMessage);
      console.error('Error rejecting change request:', err);
    }
  };

  const handleAcceptSwap = async (id: number) => {
    if (!window.confirm('Are you sure you want to accept this shift swap?')) {
      return;
    }

    try {
      await ChangeRequestService.acceptSwap(id);
      loadChangeRequests();
    } catch (err) {
      setError('Failed to accept swap');
      console.error('Error accepting swap:', err);
    }
  };

  const handleDeclineSwap = async (id: number) => {
    if (!window.confirm('Are you sure you want to decline this shift swap?')) {
      return;
    }

    try {
      await ChangeRequestService.declineSwap(id);
      loadChangeRequests();
    } catch (err) {
      setError('Failed to decline swap');
      console.error('Error declining swap:', err);
    }
  };

  const handleCreateNewRequest = async (data: CreateChangeRequestData) => {
    try {
      await ChangeRequestService.createChangeRequest(data);
      setShowNewRequestModal(false);
      loadChangeRequests();
    } catch (err) {
      setError('Failed to create change request');
      console.error('Error creating change request:', err);
    }
  };

  const renderRequestedChanges = (request: ChangeRequestWithDetails) => {
    switch (request.request_type) {
      case 'date_change':
        return (
          <>
            <Typography><strong>📅 Requested Date:</strong> {request.requested_date ? new Date(request.requested_date).toLocaleDateString() : 'Not specified'}</Typography>
            <Typography><strong>📅 Original Date:</strong> {ChangeRequestService.formatDate(request.original_start_datetime)}</Typography>
          </>
        );

      case 'time_change':
        return (
          <>
            <Typography><strong>⏰ Requested Start Time:</strong> {request.requested_start_datetime ? ChangeRequestService.formatTime(request.requested_start_datetime) : 'Not specified'}</Typography>
            <Typography><strong>⏰ Requested End Time:</strong> {request.requested_end_datetime ? ChangeRequestService.formatTime(request.requested_end_datetime) : 'Not specified'}</Typography>
            <Typography><strong>⏰ Original Time:</strong> {ChangeRequestService.formatTime(request.original_start_datetime)} - {ChangeRequestService.formatTime(request.original_end_datetime)}</Typography>
          </>
        );

      case 'shift_type_change':
        return (
          <>
            <Typography><strong>🏷️ Requested Shift Type:</strong> {request.requested_shift_type?.name || 'Not specified'}</Typography>
            <Typography><strong>🏷️ Original Shift Type:</strong> {request.original_shift_type?.name}</Typography>
          </>
        );

      case 'cancellation':
        return (
          <Typography color="danger">
            <strong>❌ Requesting to cancel this schedule entirely</strong>
          </Typography>
        );

      case 'shift_swap':
        return (
          <>
            <Typography><strong>👤 Swap Target:</strong> {request.swap_with_employee?.first_name} {request.swap_with_employee?.last_name}</Typography>
            <Typography><strong>📋 Target Schedule:</strong> {request.swap_with_schedule_id ? `Schedule #${request.swap_with_schedule_id}` : 'Not specified'}</Typography>
            {request.swap_accepted_by_target && (
              <Typography color="success">
                <strong>✅ Swap accepted by target employee</strong>
              </Typography>
            )}
          </>
        );

      default:
        return <Typography>No specific changes requested</Typography>;
    }
  };

  const getActionButtons = (request: ChangeRequestWithDetails) => {
    const buttons = [];

    // View details button
    buttons.push(
      <Tooltip key="view" title="View Details">
        <IconButton
          size="sm"
          onClick={() => handleViewDetails(request)}
          color="primary"
        >
          <ViewIcon />
        </IconButton>
      </Tooltip>
    );

    // Edit button (only for pending requests)
    if (canEdit && request.status === 'pending' && request.employee_id === user?.id) {
      buttons.push(
        <Tooltip key="edit" title="Edit Request">
                      <IconButton
                          size="sm"
            onClick={() => onNavigate?.(`/change-requests/${request.id}/edit`)}
            color="primary"
            >
            <EditIcon />
          </IconButton>
        </Tooltip>
      );
    }

    // Delete/Cancel button (only for pending requests)
    if (canDelete && request.status === 'pending' && request.employee_id === user?.id) {
      buttons.push(
        <Tooltip key="delete" title="Cancel Request">
          <IconButton
            size="sm"
            onClick={() => handleDelete(request.id)}
            color="danger"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      );
    }

    // Approve button (for managers/admins)
    if (canApprove && ['pending', 'under_review'].includes(request.status)) {
      buttons.push(
        <Tooltip key="approve" title="Approve Request">
          <IconButton
            size="sm"
            onClick={() => handleApprove(request.id)}
            color="success"
          >
            <ApproveIcon />
          </IconButton>
        </Tooltip>
      );
    }

    // Reject button (for managers/admins)
    if (canReject && ['pending', 'under_review'].includes(request.status)) {
      buttons.push(
        <Tooltip key="reject" title="Reject Request">
          <IconButton
            size="sm"
            onClick={() => handleReject(request.id)}
            color="danger"
          >
            <RejectIcon />
          </IconButton>
        </Tooltip>
      );
    }

    // Accept/Decline swap buttons (for target employee)
    if (request.request_type === 'shift_swap' && 
        request.status === 'pending' && 
        request.swap_with_employee_id === user?.id) {
      buttons.push(
        <Tooltip key="accept-swap" title="Accept Swap">
          <IconButton
            size="sm"
            onClick={() => handleAcceptSwap(request.id)}
            color="success"
          >
            <SwapIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip key="decline-swap" title="Decline Swap">
          <IconButton
            size="sm"
            onClick={() => handleDeclineSwap(request.id)}
            color="danger"
          >
            <RejectIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return buttons;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading change requests...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography level="h4" component="h1">
          Change Requests
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startDecorator={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            Filters
          </Button>
          {canCreate && (
            <Button
              variant="solid"
              startDecorator={<AddIcon />}
              onClick={() => setShowNewRequestModal(true)}
            >
              New Request
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid xs={12} sm={6} md={3}>
                <FormControl size="sm">
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={filters.status || ''}
                    onChange={(_, value) => handleFilterChange('status', value || '')}
                  >
                    <Option value="">All</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="under_review">Under Review</Option>
                    <Option value="approved">Approved</Option>
                    <Option value="rejected">Rejected</Option>
                    <Option value="cancelled">Cancelled</Option>
                    <Option value="expired">Expired</Option>
                  </Select>
                </FormControl>
                              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl size="sm">
                    <FormLabel>Request Type</FormLabel>
                  <Select
                    value={filters.request_type || ''}
                    onChange={(_, value) => handleFilterChange('request_type', value || '')}
                  >
                    <Option value="">All</Option>
                    <Option value="date_change">Date Change</Option>
                    <Option value="time_change">Time Change</Option>
                    <Option value="shift_type_change">Shift Type Change</Option>
                    <Option value="cancellation">Cancellation</Option>
                    <Option value="shift_swap">Shift Swap</Option>
                  </Select>
                </FormControl>
                              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl size="sm">
                    <FormLabel>Priority</FormLabel>
                  <Select
                    value={filters.priority || ''}
                    onChange={(_, value) => handleFilterChange('priority', value || '')}
                  >
                    <Option value="">All</Option>
                    <Option value="low">Low</Option>
                    <Option value="normal">Normal</Option>
                    <Option value="high">High</Option>
                  </Select>
                </FormControl>
                              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Button
                  variant="outlined"
                  startDecorator={<ClearIcon />}
                  onClick={handleClearFilters}
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {changeRequests.length === 0 ? (
        <Card>
          <CardContent>
            <Typography level="title-md" textAlign="center" color="neutral">
              No change requests found
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {changeRequests.map((request) => (
              <Grid xs={12} key={request.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography level="title-md" sx={{ mr: 2 }}>
                            {request.employee?.first_name} {request.employee?.last_name}
                          </Typography>
                          <Chip
                            size="sm"
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            {ChangeRequestService.formatRequestType(request.request_type)}
                          </Chip>
                          <Chip
                            size="sm"
                            sx={{
                              backgroundColor: ChangeRequestService.getStatusColor(request.status),
                              color: 'white'
                            }}
                          >
                            {request.status}
                          </Chip>
                          <Chip
                            size="sm"
                            sx={{
                              backgroundColor: ChangeRequestService.getPriorityColor(request.priority),
                              color: 'white',
                              ml: 1
                            }}
                          >
                            {request.priority}
                          </Chip>
                        </Box>
                        
                        <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                          {request.reason}
                        </Typography>
                        
                        <Typography level="body-sm" color="neutral">
                          Created: {ChangeRequestService.formatDate(request.created_at)}
                          {request.expires_at && (
                            <span style={{ marginLeft: '1rem' }}>
                              {ChangeRequestService.getTimeUntilExpiration(request.expires_at)}
                            </span>
                          )}
                        </Typography>

                        {request.request_type === 'shift_swap' && request.swap_with_employee && (
                          <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                            Swap with: {request.swap_with_employee?.first_name} {request.swap_with_employee?.last_name}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {getActionButtons(request)}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Typography level="body-sm" color="neutral">
                Page {pagination.page} of {pagination.pages}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Details Dialog */}
      <Modal
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
      >
        <ModalDialog size="lg">
        <DialogTitle>
          Change Request Details
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              {/* Request Information */}
              <Typography level="title-md" gutterBottom>
                Request Information
              </Typography>
              <Typography><strong>Employee:</strong> {selectedRequest.employee?.first_name} {selectedRequest.employee?.last_name}</Typography>
              <Typography><strong>Type:</strong> {ChangeRequestService.formatRequestType(selectedRequest.request_type)}</Typography>
              <Typography>
                <strong>Status:</strong> 
                <Chip
                  size="sm"
                  sx={{
                    backgroundColor: ChangeRequestService.getStatusColor(selectedRequest.status),
                    color: 'white',
                    ml: 1
                  }}
                >
                  {selectedRequest.status}
                </Chip>
              </Typography>
              <Typography>
                <strong>Priority:</strong> 
                <Chip
                  size="sm"
                  sx={{
                    backgroundColor: ChangeRequestService.getPriorityColor(selectedRequest.priority),
                    color: 'white',
                    ml: 1
                  }}
                >
                  {selectedRequest.priority}
                </Chip>
              </Typography>
              <Typography><strong>Reason:</strong> {selectedRequest.reason}</Typography>
              <Typography><strong>Created:</strong> {ChangeRequestService.formatDate(selectedRequest.created_at)}</Typography>
              
              {/* Original Schedule */}
              <Typography level="title-md" sx={{ mt: 3, mb: 1 }}>
                📅 Original Schedule
              </Typography>
              <Card variant="outlined" sx={{ mb: 2, borderColor: 'neutral.300' }}>
                <CardContent>
                  <Typography><strong>Employee:</strong> {selectedRequest.employee?.first_name} {selectedRequest.employee?.last_name}</Typography>
                  <Typography><strong>Shift Type:</strong> {selectedRequest.original_shift_type?.name}</Typography>
                  <Typography><strong>Date:</strong> {ChangeRequestService.formatDate(selectedRequest.original_start_datetime)}</Typography>
                  <Typography><strong>Time:</strong> {ChangeRequestService.formatTime(selectedRequest.original_start_datetime)} - {ChangeRequestService.formatTime(selectedRequest.original_end_datetime)}</Typography>
                </CardContent>
              </Card>

              {/* Requested Changes */}
              <Typography level="title-md" sx={{ mt: 3, mb: 1 }}>
                🔄 Requested Changes
              </Typography>
              <Card variant="outlined" sx={{ mb: 2, borderColor: 'primary.500' }}>
                <CardContent>
                  {renderRequestedChanges(selectedRequest)}
                </CardContent>
              </Card>

              {/* Review Information */}
              {selectedRequest.review_comments && (
                <>
                  <Typography level="title-md" sx={{ mt: 3, mb: 1 }}>
                    Review Comments
                  </Typography>
                  <Typography>{selectedRequest.review_comments}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>
            Close
          </Button>
        </DialogActions>
        </ModalDialog>
      </Modal>

      {/* New Request Modal */}
      <ChangeRequestModal
        open={showNewRequestModal}
        onClose={() => setShowNewRequestModal(false)}
        onSubmit={handleCreateNewRequest}
      />
    </Box>
  );
};

export default ChangeRequestsPage; 